import type { CSSProperties } from "react";
import type { MotionStyle, MotionValue } from "framer-motion";
import { ColorType } from "@/theme/colors";
import { GradientPreset, getGradientPresetString } from "@/theme/gradients";

const CTEAMCARD_WEBKIT_BOX_ORIENT_VERTICAL = "vertical";
const CTEAMCARD_ICON_PADDING = 6;
const CTEAMCARD_ICON_OVERLAP = -6;

enum CteamcardViewportMode {
  Mobile = "mobile",
  Desktop = "desktop",
}

export const CTEAMCARD_IMAGE_LOADING_LAZY = "lazy";
export const CTEAMCARD_IMAGE_DECODING_ASYNC = "async";
export const CTEAMCARD_MAX_PROVIDER_ICONS = 4;

export const CTEAMCARD_BACKGROUND_STYLE: CSSProperties = {
  backgroundImage: getGradientPresetString(GradientPreset.GlassyCard),
};

export const CTEAMCARD_TEXT_CLAMP_STYLE: CSSProperties = {
  width: "100%",
  display: "-webkit-box",
  WebkitBoxOrient: CTEAMCARD_WEBKIT_BOX_ORIENT_VERTICAL,
  WebkitLineClamp: 2,
  overflow: "hidden",
  textOverflow: "ellipsis",
};

export const CTEAMCARD_TOP_ROW_STYLE: CSSProperties = {
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
  justifyContent: "space-between",
  width: "100%",
  gap: 6,
};

export const CTEAMCARD_BOTTOM_ROW_STYLE: CSSProperties = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  gap: 16,
};

export const CTEAMCARD_ICONS_CONTAINER_STYLE: CSSProperties = {
  display: "flex",
  flexDirection: "row-reverse",
  alignItems: "center",
  gap: 0,
  position: "relative",
};

const CTEAMCARD_ICON_BACKDROP_STYLE: CSSProperties = {
  backdropFilter: "blur(1px)",
  WebkitBackdropFilter: "blur(1px)",
  boxShadow:
    "rgba(0, 0, 0, 0.3) 0px 2px 8px 0px, rgba(0, 0, 0, 0.15) 0px 1px 3px 0px",
};

interface CteamcardMetricsInput {
  fixedWidth?: number | string;
  fixedHeight?: number | string;
  isMobile: boolean;
  vw: number;
}

interface CteamcardResponsiveInput {
  isMobile: boolean;
}

interface CteamcardMetrics {
  cardWidth: number | string;
  cardHeight: number | string;
  contentGap: number;
}

interface CteamcardDefaultCardWidthInput {
  vw: number;
}

interface CteamcardTitleMetrics {
  titleFontSize: string;
  titlePaddingTop: string;
  categoryIconSize: number;
}

interface CteamcardViewportDefaults {
  cardHeight: number;
  contentGap: number;
  titleFontSize: string;
  titlePaddingTop: string;
  categoryIconSize: number;
  catchPhraseFontSize: string;
  providerIconSize: number;
  resolveCardWidth: (input: CteamcardDefaultCardWidthInput) => number;
}

interface CteamcardBackgroundStyleInput {
  cardBackground?: string;
}

interface CteamcardContentSurface {
  backgroundColor?: ColorType;
  backgroundOpacity?: number;
}

interface CteamcardContentMotionStyleInput {
  contentGap: number;
  contentOpacity?: number | MotionValue<number>;
}

interface CteamcardIconWrapperStyleInput {
  iconSize: number;
  useBackdropFilter: boolean;
  zIndex: number;
}

const CTEAMCARD_VIEWPORT_DEFAULTS: Record<
  CteamcardViewportMode,
  CteamcardViewportDefaults
> = {
  [CteamcardViewportMode.Mobile]: {
    cardHeight: 200,
    contentGap: 10,
    titleFontSize: "1.4rem",
    titlePaddingTop: "2px",
    categoryIconSize: 40,
    catchPhraseFontSize: "1.2rem",
    providerIconSize: 36,
    resolveCardWidth: ({ vw }) => vw * 50,
  },
  [CteamcardViewportMode.Desktop]: {
    cardHeight: 250,
    contentGap: 8,
    titleFontSize: "1.7rem",
    titlePaddingTop: "0px",
    categoryIconSize: 45,
    catchPhraseFontSize: "1.3rem",
    providerIconSize: 41,
    resolveCardWidth: () => 570,
  },
};

function getCteamcardViewportDefaults({
  isMobile,
}: CteamcardResponsiveInput): CteamcardViewportDefaults {
  const viewportMode = isMobile
    ? CteamcardViewportMode.Mobile
    : CteamcardViewportMode.Desktop;

  return CTEAMCARD_VIEWPORT_DEFAULTS[viewportMode];
}

export function getCteamcardMetrics({
  fixedWidth,
  fixedHeight,
  isMobile,
  vw,
}: CteamcardMetricsInput): CteamcardMetrics {
  const viewportDefaults = getCteamcardViewportDefaults({ isMobile });

  return {
    cardWidth: fixedWidth ?? viewportDefaults.resolveCardWidth({ vw }),
    cardHeight: fixedHeight ?? viewportDefaults.cardHeight,
    contentGap: viewportDefaults.contentGap,
  };
}

export function getCteamcardBackgroundStyle({
  cardBackground,
}: CteamcardBackgroundStyleInput): CSSProperties {
  if (cardBackground == null) {
    return CTEAMCARD_BACKGROUND_STYLE;
  }

  return { backgroundColor: cardBackground };
}

export function getCteamcardContentSurface({
  cardBackground,
}: CteamcardBackgroundStyleInput): CteamcardContentSurface {
  if (cardBackground == null) {
    return {};
  }

  return {
    backgroundColor: ColorType.Transparent,
    backgroundOpacity: 0,
  };
}

export function getCteamcardContentMotionStyle({
  contentGap,
  contentOpacity,
}: CteamcardContentMotionStyleInput): MotionStyle {
  return {
    opacity: contentOpacity ?? 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "stretch",
    gap: contentGap,
    width: "100%",
    height: "100%",
  };
}

export function getCteamcardProviderIconSize({
  isMobile,
}: CteamcardResponsiveInput): number {
  return getCteamcardViewportDefaults({ isMobile }).providerIconSize;
}

export function getCteamcardTitleMetrics({
  isMobile,
}: CteamcardResponsiveInput): CteamcardTitleMetrics {
  const viewportDefaults = getCteamcardViewportDefaults({ isMobile });

  return {
    titleFontSize: viewportDefaults.titleFontSize,
    titlePaddingTop: viewportDefaults.titlePaddingTop,
    categoryIconSize: viewportDefaults.categoryIconSize,
  };
}

export function getCteamcardCatchPhraseFontSize({
  isMobile,
}: CteamcardResponsiveInput): string {
  return getCteamcardViewportDefaults({ isMobile }).catchPhraseFontSize;
}

export function getCteamcardIconWrapperStyle({
  iconSize,
  useBackdropFilter,
  zIndex,
}: CteamcardIconWrapperStyleInput): CSSProperties {
  return {
    width: iconSize,
    height: iconSize,
    borderRadius: iconSize / 2,
    backgroundColor: "rgba(255, 255, 255, 0.11)",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    ...(useBackdropFilter ? CTEAMCARD_ICON_BACKDROP_STYLE : {}),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: CTEAMCARD_ICON_PADDING,
    marginRight: CTEAMCARD_ICON_OVERLAP,
    position: "relative",
    zIndex,
    flexShrink: 0,
  };
}
